import { forwardRef } from 'react';
import { cn } from '../../lib/utils';

interface FormInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  error?: boolean;
  validationError?: string;
  description?: string;
  required?: boolean;
  minLength?: number;
  maxLength?: number;
  pattern?: string;
}

const FormInput = forwardRef<HTMLInputElement, FormInputProps>(
  (
    { className, error, validationError, description, required, minLength, maxLength, pattern, ...props },
    ref
  ) => {
    const inputClassName = cn(
      'mt-1 block w-full rounded-xl border bg-white dark:bg-gray-900/50 px-3.5 py-2.5 text-sm shadow-soft transition-all duration-200 ease-smooth',
      'placeholder:text-gray-400 dark:placeholder:text-gray-500',
      error
        ? 'border-red-300 dark:border-red-800 focus:border-red-500 focus:ring-red-500/30'
        : 'border-gray-200 dark:border-gray-700 focus:border-primary-500 focus:ring-primary-500/20 dark:focus:border-primary-400',
      className
    );

    return (
      <div>
        <input
          ref={ref}
          className={inputClassName}
          required={required}
          minLength={minLength}
          maxLength={maxLength}
          pattern={pattern}
          aria-invalid={error}
          aria-errormessage={error ? `${props.id}-error` : undefined}
          {...props}
        />
        {description && !validationError && (
          <p className="mt-1.5 text-sm text-gray-500 dark:text-gray-400">
            {description}
          </p>
        )}
        {validationError && (
          <p id={`${props.id}-error`} className="mt-1.5 text-sm text-red-600 dark:text-red-400">
            {validationError}
          </p>
        )}
      </div>
    );
  }
);

FormInput.displayName = 'FormInput';

export { FormInput };
